import {
    APP_ENVIRONMENT,
    BAD_REQUEST,
    REQUEST_FAILED,
    SERVER_ERROR,
    UNAUTHORIZED_RESPONSE_CODE_REFRESH,
    UNAUTHORIZED_RESPONSE_CODE_REJECT
} from '../const';
import { store } from '../store';
import { logOut } from '../store/slices/authSlice';
import { gaEvents } from '../utils/analytics';
import { getAppBasename } from '../utils/helper';
import LoggerService from './LoggerService';
import RequestsApi from './RequestsApi';
import ToastifyService, { TOAST_NOTIFY_TYPES } from './ToastifyService';

class ResponseService {
    constructor() {
        this.inited = false;
    }

    init() {
        if (this.inited) {
            return;
        }

        // Prod toast policy: always show the generic line. Backend error
        // messages ("Too many requests…", "Invalid request…") and axios
        // internals ("Request failed with status code 500", "Network
        // Error") leak implementation noise that's confusing for end
        // users and unactionable from the toast alone.
        //
        // Non-prod (local, dev, staging): surface the failing METHOD +
        // URL + status + axios error code so a tester can read the
        // offending request straight off the screen. Used by every error
        // path here — SERVER_ERROR listener, BAD_REQUEST listener, and
        // the catch-all fallback — so the diagnostic shape is consistent
        // regardless of which failure mode triggered the toast.
        const isProd = APP_ENVIRONMENT === 'production' || APP_ENVIRONMENT === 'prod';
        const GENERIC_ERROR_MESSAGE = 'Something went wrong';

        // HORO-10474: the correlation id the backend sets on every response
        // (`X-Request-Id`, and mirrored into error bodies as `requestId`). This
        // is the single value that turns "a request failed for me" into a server
        // log we can actually find.
        const getRequestId = (error) => error?.response?.headers?.['x-request-id'] || error?.response?.data?.requestId || '';

        // Shared shape for every failure we log or report, so the console line,
        // the GA4 event and the toast all describe the request the same way.
        // Query string is dropped: it carries ids and occasionally tokens, and
        // the path is what identifies the endpoint.
        const describeError = (error) => {
            const url = error?.config?.url || '';
            const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
            return {
                method: (error?.config?.method || '').toUpperCase() || 'UNKNOWN',
                path: path || 'unknown URL',
                status: error?.response?.status || 0,
                code: error?.code || '',
                requestId: getRequestId(error)
            };
        };

        // Log every API failure and report it as a GA4 event. Always — including
        // production. This is the client half of the correlation: without it a
        // user-visible failure leaves no trace anywhere, which is exactly how a
        // disabled feature flag turned into "it went back to the homepage".
        const reportError = (label, error) => {
            const details = describeError(error);
            LoggerService.warn(`[api] ${label}`, details);
            gaEvents.apiError(details);
            return details;
        };

        const buildErrorToast = (error, fallback = GENERIC_ERROR_MESSAGE) => {
            if (isProd) return GENERIC_ERROR_MESSAGE;
            const baseMessage = error?.response?.data?.error || fallback;
            if (!error) return baseMessage;
            const { method, path, status, code, requestId } = describeError(error);
            const statusPart = status ? ` ${status}` : '';
            const codePart = code ? ` ${code}` : '';
            // The request id goes in the toast so a tester can quote it in a bug
            // report instead of describing what they were doing.
            const idPart = requestId ? ` [${requestId}]` : '';
            return `${baseMessage} — ${method} ${path}${statusPart}${codePart}${idPart}`.trim();
        };

        // A 401/403 only means "the session is invalid" when the request
        // that got it was actually authenticated (carried a bearer token)
        // — that's the case this force-logout-and-reload exists for. The
        // same two status codes are also the normal, expected failure
        // shape for auth routes that never had a session to begin with:
        // wrong email/password on sign-in (401), an unverified account on
        // sign-in (403), an invalid code on reset-password (now 400, but
        // was 401 until that was fixed at the source). Those calls carry
        // no Authorization header, so treating their 401/403 as "log the
        // user out and hard-reload the page" wiped whatever screen they
        // were on — kicking them out of the sign-in modal on a plain wrong
        // password — even though the component itself already shows the
        // error inline and expects to stay put. Gating on whether a token
        // was actually sent fixes this without weakening the real
        // expired-session case, which always did carry one.
        const wasAuthenticatedRequest = (error) => Boolean(error?.config?.headers?.Authorization);

        const listeners = {
            [UNAUTHORIZED_RESPONSE_CODE_REFRESH]: (error) => {
                if (!wasAuthenticatedRequest(error)) {
                    return;
                }
                reportError('401 refresh', error);
                store.dispatch(logOut(true));
            },
            [UNAUTHORIZED_RESPONSE_CODE_REJECT]: (error) => {
                if (!wasAuthenticatedRequest(error)) {
                    return;
                }
                // HORO-10599: a 403 with `kind: 'advisor_locked'` is a
                // legitimate, expected business-rule refusal on a perfectly
                // valid, still-authenticated session — an anonymous-viewer-
                // turned-registered account whose chat with a subscriber-
                // exclusive advisor was never actually entitled, or the
                // same gate on chat CREATION for any advisor outside the
                // registered-tier roster (a routine, everyday catalog tap,
                // not a rare case) — not a sign the token itself was
                // rejected. This listener existed for the latter (an
                // actually-invalid session).
                //
                // Zara's own 3-message trial cap USED to also throw this
                // exact shape and got caught by this bug — logging a
                // registered-free account straight out the moment they hit
                // their 3rd message, the intended, guaranteed-to-happen-
                // to-everyone outcome of the trial, not the rare edge case
                // this logout-and-reload was built around. It now has its
                // own dedicated 429 `kind: 'zara_trial_exhausted'` response
                // instead (chat/index.ts), which never reaches this
                // listener at all — so this exemption is no longer doing
                // double duty for it, just for the advisor_locked cases it
                // was always meant for.
                //
                // The request still rejects normally below either way, so
                // the caller's own handling (the upsell modal) still runs —
                // this only skips the unrelated forced logout.
                // `error.response.data` is the backend's whole JSON body
                // (`{ error, data: { kind, cta }, success }`), so `kind`
                // lives one level deeper than it looks — but this codebase
                // already has precedent (the tarot-limit checks elsewhere)
                // for that nesting being inconsistent across routes/error
                // paths, so both are checked rather than assuming one.
                const kind = error?.response?.data?.data?.kind || error?.response?.data?.kind;
                if (kind === 'advisor_locked') {
                    return;
                }
                reportError('403 reject', error);
                store.dispatch(logOut(true));
            },
            [REQUEST_FAILED]: (error) => {
                // HORO-10474: log BEFORE navigating away. This redirect is the
                // most hostile failure path in the app — the page is replaced,
                // the console is wiped, and the only thing a user can report is
                // "it went back to the homepage". A disabled feature flag 404s
                // here, which is how HORO-10471 presented.
                //
                // NOTE: whether a 404 on one XHR should discard the page at all
                // is a product decision left open by HORO-10474 item 15 — some
                // flows (a deleted chat) may rely on it, so the behaviour is
                // unchanged here on purpose. Only the silence is fixed. A toast
                // would not help: the page is replaced before it can be read,
                // which is why the console line and the GA4 event are the record.
                reportError('404 → redirect to homepage', error);

                window.location.replace(getAppBasename());
            },
            [SERVER_ERROR]: (error) => {
                reportError('500', error);
                ToastifyService.setToast(buildErrorToast(error), TOAST_NOTIFY_TYPES.ERROR);
            },
            [BAD_REQUEST]: (error) => {
                // Prod: generic. Non-prod: backend's `data.error` plus
                // METHOD/URL/status suffix via the shared builder, so
                // validation messages still come through verbatim during
                // triage but show the same diagnostic shape as the other
                // listeners.
                //
                // Logged but deliberately NOT sent to GA4: a validation 400 is
                // the request being wrong, not the app being broken, and it is
                // common enough to swamp a rate that should mean "something is
                // failing". The backend logs these at info for the same reason.
                LoggerService.warn('[api] 400', describeError(error));
                ToastifyService.setToast(buildErrorToast(error), TOAST_NOTIFY_TYPES.ERROR);
            }
        };

        RequestsApi.addResponseListener(
            async (response) => {
                // A 4xx/5xx that arrives as a resolved response (axios only
                // rejects per `validateStatus`) still needs to look like an error
                // to the listeners, or it logs as "unknown URL".
                listeners[response.status]?.({ config: response?.config, response });
                return response;
            },
            async (error) => {
                // Intentional cancellations are not failures — never toast
                // them. The homepage typeahead (useTypeahead) aborts its
                // in-flight `/suggestions` request whenever the query changes
                // or the page unmounts (e.g. navigating into chat after
                // picking a suggested question), which surfaces here as an
                // axios ERR_CANCELED / CanceledError. Swallow silently.
                if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                    return Promise.reject(error);
                }

                // Opt-out: the caller shows its own message for this request, so
                // skip the generic toast and the 404 → homepage redirect.
                // 401/403 still reach the session handling above.
                if (
                    error?.config?.headers?.['X-FE-Handle-Errors'] === '1' &&
                    error?.response?.status !== 401 &&
                    error?.response?.status !== 403
                ) {
                    return Promise.reject(error);
                }

                // Opt-out: some background fetches (e.g., the tab-resume
                // chat-messages resync in ChatBar) attach this header to
                // request that transient `ERR_NETWORK` failures stay
                // silent — the user didn't initiate them and a toast on
                // a fetch they can't see would be confusing. The caller
                // is expected to handle the rejection (typically with
                // its own retry) and surface a real error if it persists.
                const silentNetwork = error?.config?.headers?.['X-FE-Silent-Network-Error'] === '1';
                if (silentNetwork && error?.code === 'ERR_NETWORK') {
                    return Promise.reject(error);
                }

                (await typeof listeners[error?.response?.status]) === 'function'
                    ? listeners[error?.response?.status](error)
                    : ToastifyService.setToast(buildErrorToast(error), TOAST_NOTIFY_TYPES.ERROR);

                return Promise.reject(error);
            }
        );

        this.inited = true;
    }
}

export default new ResponseService();
